import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ImportDataService } from 'app/core/services/import-data.service';

@Component({
  selector: 'modal-import-timeentries',
  templateUrl: './modal-import-timeentries.component.html',
  styleUrls: ['./modal-import-timeentries.component.scss']
})
export class ModalImportTimeentriesComponent implements OnInit, AfterViewInit {

  isLoading: boolean = false;
  formatError: boolean = false;

  insertionMessage: string = null;
  successCount: number = 0;
  failureCount: number = 0;
  skippedCount: number = 0;
  totalEntries: number = 0;
  isInserting = false;
  displayTable = true;
  isImported = false;
  showResults = false;

  @Input() importedData: any[] = [];
  @Output() importCompleted = new EventEmitter<boolean>();

  selectedEntries: any[] = [];
  dataSource: MatTableDataSource<any>;
  resultsDataSource: MatTableDataSource<any>;

  displayedColumns: string[] = [
    'select', 'employeeId', 'timeIn', 'timeOut'
  ];

  resultColumns: string[] = [
    'employeeId', 'timeIn', 'timeOut', 'status', 'message'
  ];

  expectedHeaders: string[] = [
    'Employee_ID', 'Time_In', 'Time_Out'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('resultsPaginator') resultsPaginator!: MatPaginator;

  constructor(
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private importDataService: ImportDataService
  ) { }

  ngOnInit(): void {
    if (!this.isDataFormatCorrect()) {
      this.formatError = true;
    }

    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedEntries = [...this.importedData];
  }

  ngAfterViewInit(): void {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  isDataFormatCorrect(): boolean {
    if (!this.importedData || this.importedData.length === 0) return true;
    const importedHeaders = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(header => importedHeaders.includes(header));
  }

  onCheckboxChange(entry: any, isChecked: boolean): void {
    if (isChecked) {
      this.selectedEntries.push(entry);
    } else {
      const index = this.selectedEntries.indexOf(entry);
      if (index !== -1) {
        this.selectedEntries.splice(index, 1);
      }
    }
  }

  isSelected(entry: any): boolean {
    return this.selectedEntries.includes(entry);
  }

  toggleSelectAll(isChecked: boolean): void {
    if (isChecked) {
      this.selectedEntries = [...this.importedData];
    } else {
      this.selectedEntries = [];
    }
  }

  saveSelectedEntries(): void {
    this.totalEntries = this.selectedEntries.length;
    this.isInserting = true;
    this.displayTable = false;
    this.isLoading = true;

    this.insertionMessage = "Processing time entries, please wait...";

    this.importDataService.insertTimeEntriesFromCSV(this.selectedEntries).subscribe({
      next: (response) => {
        this.successCount = response.successCount;
        this.failureCount = response.failureCount;
        this.skippedCount = response.skippedCount || 0;

        let message = `Import completed: ${this.successCount} successful`;
        if (this.skippedCount > 0) {
          message += `, ${this.skippedCount} skipped (duplicates)`;
        }
        if (this.failureCount > 0) {
          message += `, ${this.failureCount} failed`;
        }

        this.insertionMessage = message;

        if (this.failureCount > 0 || this.skippedCount > 0) {
          this.resultsDataSource = new MatTableDataSource(response.results);
          setTimeout(() => {
            if (this.resultsPaginator) {
              this.resultsDataSource.paginator = this.resultsPaginator;
            }
          });
          this.showResults = true;

          // Auto-download CSV with results
          this.downloadResultsCSV(response.csvData);
        } else {
          this.isImported = true;
        }

        this.isInserting = false;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('An error occurred during the insertion process:', err);
        this.insertionMessage = `Error inserting time entries. Please try again.`;
        this.isInserting = false;
        this.isLoading = false;
      }
    });
  }

  downloadResultsCSV(csvData: string): void {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Time_Entries_Import_Results_${new Date().getTime()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  downloadTemplate(): void {
    const csvFilePath = 'assets/csv/Time_Entry_Template.csv';

    this.http.get(csvFilePath, { responseType: 'blob' }).subscribe((data: Blob) => {
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'Time_Entry_Template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }

  retryFailed(): void {
    const failedEntries = this.resultsDataSource.data.filter(r => r.Status === 'Failed');
    this.selectedEntries = failedEntries.map(r => ({
      Employee_ID: r.Employee_ID,
      Time_In: r.Time_In,
      Time_Out: r.Time_Out
    }));

    this.showResults = false;
    this.displayTable = true;
    this.dataSource = new MatTableDataSource(this.selectedEntries);
    setTimeout(() => {
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
    });
  }

  onClose(): void {
    // Emit event to notify parent component that import is completed
    if (this.successCount > 0) {
      this.importCompleted.emit(true);
    }
    this.activeModal.close('import-completed');
  }
}